import { SchoolHeader } from "components/ShoolHeader/schoolHeader";
import { useState, useRef } from "react";

export function Post({
    id,
    title,
    description,
    imageUrl,
    date,
    location,
    likes,
    comments,
    shares,
    onLike,
    onComment,
    onShare,
    onRSVP,
    badge,
    media_url,
    base_URL,
    feedTitle = "Our Social Feed",
    feedDescription = "Join our community to see amazing content and stay connected!",
  }) {
    const [isLiked, setIsLiked] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isVideoLoading, setIsVideoLoading] = useState(true);
    const [videoError, setVideoError] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [showShareOptions, setShowShareOptions] = useState(false);
    const videoRef = useRef(null);

    const images = media_url?.map(url => `${base_URL}/get-image?objectKey=${url}`) || [imageUrl];

    const getShareUrl = () => {
      return `${window.location.origin}/share/${id}`;
    };

    const nextImage = () => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    };

    const prevImage = () => {
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    const handleVideoLoad = () => {
      console.log("Video loaded successfully:", {
        src: videoRef.current?.currentSrc,
        readyState: videoRef.current?.readyState,
        networkState: videoRef.current?.networkState
      });
      setIsVideoLoading(false);
      setVideoError(false);

      // Attempt to play the video
      if (videoRef.current) {
        videoRef.current.play().catch(error => {
          console.log("Auto-play failed:", error);
        });
      }
    };

    const handleVideoError = (e) => {
      console.log("Video Error Details:", {
        error: e.target.error,
        networkState: e.target.networkState,
        readyState: e.target.readyState,
        src: e.target.currentSrc
      });
      setIsVideoLoading(false);
      setVideoError(true);
    };

    const toggleVideoPlayback = () => {
      if (videoRef.current) {
        if (videoRef.current.paused) {
          videoRef.current.play();
        } else {
          videoRef.current.pause();
        }
      }
    };

    const toggleMute = () => {
      if (videoRef.current) {
        videoRef.current.muted = !videoRef.current.muted;
        setIsMuted(!isMuted);
      }
    };

    const handleShare = () => {
      if (navigator.share) {
        navigator.share({
          title: title,
          text: description,
          url: getShareUrl()
        }).catch(console.error);
      } else {
        setShowShareOptions(true);
      }
    };

    const copyLink = () => {
      const shareText = `${title}\n\n${description}\n\n`;
      navigator.clipboard.writeText(shareText + getShareUrl())
        .then(() => {
          setShowShareOptions(false);
        })
        .catch(console.error);
    };

    const shareToFacebook = () => {
      const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl())}&quote=${encodeURIComponent(`${title}\n\n${description}`)}`;
      window.open(shareUrl, '_blank', 'width=600,height=400');
    };

    const shareToTwitter = () => {
      const shareText = `${title}\n\n${description}`;
      const shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(getShareUrl())}&text=${encodeURIComponent(shareText)}`;
      window.open(shareUrl, '_blank', 'width=600,height=400');
    };

  return (
    <>

         <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform duration-200 hover:shadow-lg print:shadow-none print:border print:border-gray-200">
        <div className="flex flex-col md:flex-row min-h-[500px] md:h-[500px] print:flex-col print:min-h-0">
          {/* Media Section - Full width on mobile, 60% on desktop */}
          {images.length > 0 && (
            <div className="w-full md:w-3/5 relative h-[300px] md:h-full print:w-full print:h-[400px]">
              {images[currentImageIndex]?.match(/\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv|m4v)$/i) ? (
                <div className="relative w-full h-full print:hidden">
                  {isVideoLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    </div>
                  )}
                  {videoError && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                      <div className="text-red-500 text-center">
                        <i className="fas fa-exclamation-circle text-2xl mb-2"></i>
                        <p>Failed to load video</p>
                      </div>
                    </div>
                  )}

                  <video
                    ref={videoRef}
                    className="w-full h-full object-contain bg-black"
                    onLoadedData={handleVideoLoad}
                    onError={(e) => handleVideoError(e)}
                    onCanPlay={() => console.log("Video can play")}
                    onWaiting={() => console.log("Video is waiting for data")}
                    onStalled={() => console.log("Video playback stalled")}
                    loop
                    playsInline
                    controls={false}
                    muted
                    preload="auto"
                    crossOrigin="anonymous"
                  >
                    <source src={images[currentImageIndex]} type="video/mp4" />
                    <source src={images[currentImageIndex]} type="video/webm" />
                    <source src={images[currentImageIndex]} type="video/ogg" />
                    Your browser does not support the video tag.
                  </video>
                  <div className="absolute bottom-4 right-4 flex gap-2">
                    <button
                      onClick={toggleMute}
                      className="bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-colors"
                    >
                      <i className={`fas ${isMuted ? 'fa-volume-mute' : 'fa-volume-up'}`}></i>
                    </button>
                    <button
                      onClick={toggleVideoPlayback}
                      className="bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-colors"
                    >
                      <i className={`fas ${videoRef.current?.paused ? 'fa-play' : 'fa-pause'}`}></i>
                    </button>
                    <button
                      onClick={handleShare}
                      className="bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-colors"
                    >
                      <i className="fas fa-share"></i>
                    </button>
                  </div>
                  {showShareOptions && (
                    <div className="absolute bottom-16 right-4 bg-white rounded-lg shadow-lg p-4 w-64">
                      <div className="space-y-2">
                        <div className="px-3 py-2 border-b border-gray-100">
                          <h3 className="font-semibold text-gray-900">{title}</h3>
                          <p className="text-sm text-gray-500">{description}</p>
                        </div>
                        <button
                          onClick={copyLink}
                          className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded-md flex items-center gap-2"
                        >
                          <i className="fas fa-link"></i>
                          Copy Feed Link
                        </button>
                        <button
                          onClick={shareToFacebook}
                          className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded-md flex items-center gap-2"
                        >
                          <i className="fab fa-facebook text-blue-600"></i>
                          Share Feed on Facebook
                        </button>
                        <button
                          onClick={shareToTwitter}
                          className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded-md flex items-center gap-2"
                        >
                          <i className="fab fa-twitter text-blue-400"></i>
                          Share Feed on Twitter
                        </button>
                        <button
                          onClick={() => setShowShareOptions(false)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded-md flex items-center gap-2 text-red-500"
                        >
                          <i className="fas fa-times"></i>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <img
                  src={images[currentImageIndex]}
                  alt={title}
                  className="w-full h-full object-cover print:object-contain"
                  loading="lazy"
                />
              )}
              {images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
                  >
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
                  >
                    <i className="fas fa-chevron-right"></i>
                  </button>
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                    {images.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Content Section - Full width on mobile, 40% on desktop */}
          <div className="flex-1 p-4 md:p-6 overflow-y-auto print:p-8">
            <div className="flex items-center justify-between mb-3 md:mb-4 print:mb-6">
              <div className="flex items-center text-gray-500 text-xs md:text-sm print:text-base">
                <span>{ new Date(date)?.toLocaleDateString()}</span>
                <i className="fas fa-circle text-[3px] mx-2"></i>
                <span>{location}</span>
              </div>
            </div>

            {badge && (
              <div className="mb-2 md:mb-3 print:mb-4">
                <span className={`inline-block px-2.5 py-1 rounded-full text-xs md:text-sm font-medium ${badge.color} print:border print:border-current`}>
                  {badge.text}
                </span>
              </div>
            )}

            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-3 print:text-3xl print:mb-4">{title}</h2>
            <p className="text-base md:text-lg text-gray-600 mb-4 md:mb-6 leading-relaxed print:text-lg print:mb-8">{description}</p>

            {/* <div className="flex items-center justify-between py-3 md:py-4 border-t border-gray-100 print:border-t-2 print:py-6">
              <div className="flex items-center space-x-4">
                <span className="text-gray-500 text-xs md:text-sm print:text-base">
                  <i className="fas fa-thumbs-up text-blue-500 mr-1"></i>
                  {likes} Likes
                </span>
                <span className="text-gray-500 text-xs md:text-sm print:text-base">
                  <i className="fas fa-comment text-blue-500 mr-1"></i>
                  {comments} Comments
                </span>
                <span className="text-gray-500 text-xs md:text-sm print:text-base">
                  <i className="fas fa-share text-blue-500 mr-1"></i>
                  {shares} Shares
                </span>
              </div>
            </div> */}

            <div className="flex gap-2 md:gap-3 pt-3 md:pt-4 print:hidden">
              {/* <button
                onClick={() => setIsLiked(!isLiked)}
                className={`flex-1 flex items-center justify-center px-4 md:px-6 py-2 md:py-2.5 rounded-lg transition-colors text-sm md:text-base ${
                  isLiked
                    ? "text-blue-600 bg-blue-50 hover:bg-blue-100"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                <i
                  className={`fas fa-thumbs-up mr-2 ${isLiked ? "text-blue-600" : "text-gray-400"}`}
                ></i>
                Like
              </button> */}

              <button
                onClick={handleShare}
                className="flex-1 flex items-center justify-center px-4 md:px-6 py-2 md:py-2.5 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors text-sm md:text-base"
              >
                <i className="fas fa-share mr-2 text-gray-400"></i>
                Share
              </button>
            </div>
          </div>
        </div>

        <style jsx global>{`
          @media print {
            @page {
              margin: 2cm;
            }

            body * {
              visibility: hidden;
            }

            .print\\:shadow-none,
            .print\\:shadow-none * {
              visibility: visible;
            }

            .print\\:shadow-none {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }

            /* Hide interactive elements */
            button,
            .hover\\:shadow-lg,
            .hover\\:bg-gray-50,
            .hover\\:bg-blue-700 {
              display: none !important;
            }

            /* Ensure text is black for better printing */
            .text-gray-500,
            .text-gray-600,
            .text-gray-900 {
              color: #000 !important;
            }

            /* Add URL and date to the bottom of the page */
            .print\\:shadow-none::after {
              content: "Printed from: " attr(data-url) " on " attr(data-date);
              display: block;
              text-align: center;
              font-size: 12px;
              color: #666;
              margin-top: 20px;
              border-top: 1px solid #ddd;
              padding-top: 10px;
            }
          }
        `}</style>
      </div>
      </>

    );
  }

  function StoryComponent() {
    const samplePost = {
      id: "1",
      title: "Annual Alumni Meet 2025",
      description:
        "Join us for an evening of networking, reminiscing, and celebrating our alma mater. We'll have special guests, entertainment, and great food!",
      imageUrl: "/alumni-meet.jpg",
      date: "March 15, 2025",
      location: "Grand Hotel, New York",
      likes: 128,
      comments: 45,
      shares: 23,
      author: {
        name: "Alumni Association",
        avatar: "/avatar.jpg",
      },
    };

    return (
      <div className="max-w-2xl mx-auto p-4 bg-gray-50">
        <MainComponent
          {...samplePost}
          onLike={() => {}}
          onComment={() => {}}
          onShare={() => {}}
          onRSVP={() => {}}
        />
      </div>
    );
  }

  <style jsx global>{`
    .hover\:shadow-lg {
      transition: box-shadow 0.2s ease-in-out;
    }

    .hover\:bg-gray-50 {
      transition: background-color 0.15s ease-in-out;
    }

    .hover\:bg-blue-700 {
      transition: background-color 0.15s ease-in-out;
    }
  `}</style>;


